import React, { useCallback, useEffect, useState } from 'react';
import {
  InformationCircleIcon, CheckIcon, DocumentDuplicateIcon,
  PhoneIcon, EnvelopeIcon, MapPinIcon, ArrowDownTrayIcon,
} from '@heroicons/react/24/solid';
import {
  Anchor, Avatar, Button, Modal, Tooltip,
} from '@components/common';
import useWindowDimensions from '@hooks/useMediaQuery';
import { useCopyToClipboard } from '@hooks/common/useCopyToClipboard';
import { useTranslation } from 'next-i18next';
import { HireSalaryModal } from '@components/templates/cv/partials/modals/HireSalaryModal';
import { fetcher, isApiV1Errors } from '@utils/data-fetching';
import sendGoogleTagEvent from '@hooks/useSendGoogleTagEvent';
import { route } from '@services/symfony';
import { useStaticToasts } from '@hooks/useStaticToasts';
import { ResumeSocialIconById } from '@components/templates/profile/common/ResumeSocialIconById';
import { locationPreferencesKeys } from '@utils/resume/job-type-preferences';
import { useSearchParams } from 'next/navigation';
import { clsx } from 'clsx';
import { ApplicantStatusEnum, StatusChangeActorEnum } from '@type/v1-api-types';
import {
  CvDetailAvailableOperationsCurrentStatus,
  CvDetailObject, CvDetailPreferences,
  IUnpaidInvoiceInterface,
} from '../interface/CvDetailInterface';


function IconUnpaidOrExpired() {
  return (
    <svg className="size-8" viewBox="0 0 32 33" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect y="0.5" width="32" height="32" rx="16" fill="#FFC107" />
      <path d="M24.7812 20.7812L17.2812 7.75C16.7188 6.78125 15.25 6.75 14.6875 7.75L7.1875 20.7812C6.625 21.75 7.34375 23 8.5 23H23.4688C24.625 23 25.3438 21.7812 24.7812 20.7812ZM16 18.0625C16.7812 18.0625 17.4375 18.7188 17.4375 19.5C17.4375 20.3125 16.7812 20.9375 16 20.9375C15.1875 20.9375 14.5625 20.3125 14.5625 19.5C14.5625 18.7188 15.1875 18.0625 16 18.0625ZM14.625 12.9062C14.5938 12.6875 14.7812 12.5 15 12.5H16.9688C17.1875 12.5 17.375 12.6875 17.3438 12.9062L17.125 17.1562C17.0938 17.375 16.9375 17.5 16.75 17.5H15.2188C15.0312 17.5 14.875 17.375 14.8438 17.1562L14.625 12.9062Z" fill="white" />
    </svg>
  );
}

export interface InterfaceJobFolderOptions {
  jobId:number;
  title:string
  jobSlug:string
}

export const ProfileHeading = ({
  cvDetail,
  jobFolderSlug,
  currentCVStatus,
  applicantSeenContactStatus,
  phoneHighlight,
  openLinksInNewTab = false,
  isCompany,
  locale,
  onDownloadCv,
}: { cvDetail:CvDetailObject;
  jobFolderSlug:string;
  applicantSeenContactStatus:boolean;
  phoneHighlight:boolean;
  currentCVStatus:CvDetailAvailableOperationsCurrentStatus | undefined;
  openLinksInNewTab?:boolean;
  isCompany?: boolean;
  locale: Locale;
  onDownloadCv?: () => void;
}) => {
  const { t } = useTranslation('common');

  const { isMobile } = useWindowDimensions();
  const phoneNumber = cvDetail?.privateInformation?.phone || '';
  const { handleCopyText: handleCopyPhone, isCopied: isPhoneCopied } = useCopyToClipboard(phoneNumber, true, 'global.clipboard.phone-copied');

  const [unpaidInvoice, setUnpaidInvoice] = useState<IUnpaidInvoiceInterface>();
  const [salaryModal, setSalaryModal] = useState<boolean>(false);
  const [hasBeenConfirmedFromHiredModal, setHasBeenConfirmedFromHiredModal] = useState<boolean>(false);
  const applicantID = cvDetail?.id ? cvDetail.id : 0;
  const [isAllowedShortData, setIsAllowedShortData] = useState<boolean>(applicantSeenContactStatus);
  const rid = useSearchParams()?.get('rid');
  const { showStatusToast } = useStaticToasts();
  const availableForRelocation = cvDetail?.preferences?.some((item: CvDetailPreferences) => item?.value === t(locationPreferencesKeys.relocation));

  const isShortlist = currentCVStatus?.id === String(ApplicantStatusEnum.SHORTLIST);
  const actor = cvDetail?.application?.statusChangeActor;


  const fetchCompanyStatus = useCallback(() => {
    fetcher('/v1/company/get-status', locale, {
      method: 'GET',
      stringifyPayload: true,
    }).then((response) => {
      setUnpaidInvoice(response as IUnpaidInvoiceInterface);
    }).catch(() => {});
  }, [locale]);

  useEffect(() => {
    if (isCompany) {
      fetchCompanyStatus();
    }
  }, [fetchCompanyStatus, isCompany]);

  // If the Resume changes then priority of contacted data is set to resume object else we
  // get the trigger from Call button
  useEffect(() => {
    const condition = typeof cvDetail?.application?.applicantSeenContact === 'boolean' ? cvDetail?.application?.applicantSeenContact : false;
    setIsAllowedShortData(condition);
  }, [cvDetail]);

  useEffect(() => {
    setIsAllowedShortData(applicantSeenContactStatus);
  }, [applicantSeenContactStatus]);

  const succesfullHiredModal = () => {
    setSalaryModal(false);
    setHasBeenConfirmedFromHiredModal(true);
  };

  let cvStatusParentColor;
  let cvStatusIconColor;

  switch (currentCVStatus?.id) {
    case '3':
      cvStatusParentColor = ' text-pink';
      cvStatusIconColor = ' bg-pink/10 ';
      break;
    case '21':
      cvStatusParentColor = ' text-primary';
      cvStatusIconColor = ' bg-primary/10 ';
      break;
    case '4':
      cvStatusParentColor = ' text-error';
      cvStatusIconColor = ' bg-error/10';
      break;
    case '2':
      cvStatusParentColor = ' text-secondary';
      cvStatusIconColor = ' bg-secondary/10';

      break;
    case '22':
      cvStatusParentColor = ' text-secondary';
      cvStatusIconColor = ' bg-secondary/10';
      break;
    case '23':
      cvStatusParentColor = ' text-warning';
      cvStatusIconColor = ' bg-warning/20';
      break;
    default:
      cvStatusParentColor = ' text-primary';
      cvStatusIconColor = ' bg-primary/10';
  }

  const sendContactedData = () => {
    fetcher(`/v1/resume-operation/${applicantID}/contact?contact_type=7${rid ? `&rid=${rid}` : ''}`, locale, {
      method: 'POST',
      payload: {
        type: 'personal_info',
        jobSlug: jobFolderSlug,
      },
      stringifyPayload: true,
    }).then((response) => {
      if (isApiV1Errors(response)) {
        const { code } = response.errors[0];
        showStatusToast(String(`${code}`));
      } else {
        setIsAllowedShortData(true);
      }
    }).catch(() => {
    });
  };


  let badgeRingClasses = '';
  if (isShortlist && actor === StatusChangeActorEnum.AUTO) {
    badgeRingClasses = 'ring-2 ring-secondary p-1 rounded-full';
  } else if (isShortlist && actor === StatusChangeActorEnum.ADMIN) {
    badgeRingClasses = 'ring-2 ring-warning/20 p-1 rounded-full';
  }

  let sourceLabel: string | undefined;
  if (isShortlist && actor === StatusChangeActorEnum.AUTO) {
    sourceLabel = t('shortlisted_source_bestie');
  } else if (isShortlist && actor === StatusChangeActorEnum.ADMIN) {
    sourceLabel = t('shortlisted_source_agent');
  }


  return (
    <>
      <section className="">

        <div className="block">

          {/* Recruiter-context labels (interest, active_user, interview-request, applicant status badge).
              Hidden when the user is viewing their own CV via "View profile" — none of these apply. */}
          {!cvDetail?.isOwnCV && (
            <div className="mb-5 flex items-center justify-between">

              <div className="flex flex-col gap-y-1">
                {cvDetail?.interest
                  && (
                    <span className="text-sm text-ink-medium">
                      {cvDetail?.interest?.value}
                    </span>
                  )}

                <div className="flex flex-wrap gap-x-3 gap-y-1">
                  {cvDetail?.activityStatus !== undefined
                    && (
                      <span className="flex min-w-fit items-center text-sm text-ink-medium">
                        <InformationCircleIcon width={14} className="mr-1 text-secondary" />
                        {t('cv.detail.active_user')}
                      </span>
                    )}

                  {(cvDetail?.application?.interviewRequest !== undefined)
                    && (
                      <span className="flex min-w-fit items-center rounded-2xl bg-warning/20 px-3 py-1 text-sm !text-orange-600">
                        {cvDetail.application.interviewRequest.status || t('cv.detail.action.validate.requested')}
                      </span>
                    )}
                </div>
              </div>


              {currentCVStatus !== undefined && (
                <div className={`flex items-center font-semibold ${cvStatusParentColor}`}>
                  {sourceLabel ? (
                    <Tooltip
                      tooltipText={sourceLabel}
                      position="top"
                      floating
                      triggerContainerClass="inline-flex"
                    >
                      <span className={clsx('inline-flex', badgeRingClasses)}>
                        <div
                          className={`w-full items-center justify-center rounded-2xl p-1 text-sm ${cvStatusIconColor}`}
                        >
                          {currentCVStatus?.name}
                        </div>
                      </span>
                    </Tooltip>
                  ) : (
                    <span className={clsx('inline-flex', badgeRingClasses)}>
                      <div
                        className={`w-full items-center justify-center rounded-2xl px-4 py-1 text-sm ${cvStatusIconColor}`}
                      >
                        {currentCVStatus?.name}
                      </div>
                    </span>
                  )}
                </div>
              )}

            </div>
          )}

          <div className="flex flex-col gap-4 py-5 lg:flex-row lg:items-start lg:gap-6">
            <div id="about-me" className="relative shrink-0 self-center lg:self-start">
              {/* Avatar */}
              <Avatar
                size="xl"
                name={cvDetail?.privateInformation?.name}
                imgPath={cvDetail?.imgUrl}
                imgFolder="resume_image"
                extractImageName
                imgPriority
                withRing
                alt="Profile picture"
                className="rounded-full !outline-2 !outline-offset-3 !outline-secondary !ring-4"
              />
            </div>

            <div className="flex-1 border-b pb-4 text-center lg:text-left">
              {cvDetail?.privateInformation && (
                <h2 className="text-xl font-bold">{cvDetail.privateInformation.name}</h2>
              )}

              {/* If the CV is not unblocked then show the lead experience as title */}
              {!cvDetail?.accessToPrivateInformation && cvDetail?.leadExperience && (
                <h2 className="text-xl font-bold">{cvDetail.leadExperience.title}</h2>
              )}

              {cvDetail?.leadExperience && (
                <p className="block text-sm font-normal text-ink-medium">
                  {cvDetail.leadExperience.title}
                  {cvDetail.leadExperience.company && ` - ${cvDetail.leadExperience.company}`}
                </p>
              )}

              <div className="mt-3">
                <div className="flex flex-col items-center gap-y-1 text-sm font-normal text-ink-medium lg:items-start">
                  {cvDetail?.location && (
                    <div className="flex items-center gap-1">
                      <MapPinIcon className="size-4 shrink-0 text-ink-medium" />
                      <span>
                        {cvDetail.location}
                        {availableForRelocation && ` (${t(locationPreferencesKeys.relocation)})`}
                        {cvDetail.age && `, ${cvDetail.age} ${t('cv.card.label.years')}`}
                        {cvDetail.gender && `, ${cvDetail.gender}`}
                      </span>
                    </div>
                  )}

                  {(isAllowedShortData || cvDetail?.isOwnCV) && (
                    <>
                      {cvDetail?.privateInformation?.phone && (
                        <div
                          className={`flex items-center gap-1 transition-all ${phoneHighlight ? 'rounded-sm bg-info px-2 text-surface' : 'text-primary'}`}
                        >
                          <PhoneIcon className="size-4 shrink-0 text-ink-medium" />
                          {isMobile ? (
                            <a
                              onClick={() => sendGoogleTagEvent('employer_cv_phone_call_clicked', 'Custom')}
                              href={`tel:${cvDetail.privateInformation.phone}`}
                              className="inline-block text-sm font-normal"
                            >
                              {cvDetail.privateInformation.phone}
                            </a>
                          ) : (
                            <span
                              role="button"
                              tabIndex={0}
                              onClick={() => {
                                sendGoogleTagEvent('employer_cv_phone_call_clicked', 'Custom');
                                handleCopyPhone();
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  sendGoogleTagEvent('employer_cv_phone_call_clicked', 'Custom');
                                  handleCopyPhone();
                                }
                              }}
                              className="inline-flex cursor-pointer items-center gap-1 text-sm font-normal"
                            >
                              {cvDetail.privateInformation.phone}
                              {isPhoneCopied ? (
                                <CheckIcon className="size-4" />
                              ) : (
                                <DocumentDuplicateIcon className="size-4" />
                              )}
                            </span>
                          )}
                        </div>
                      )}

                      {cvDetail?.privateInformation?.email && (
                        <div className="flex items-center gap-1 text-primary">
                          <EnvelopeIcon className="size-4 shrink-0 text-ink-medium" />
                          <a
                            onClick={() => sendGoogleTagEvent('employer_cv_email_send_clicked', 'Custom')}
                            href={`mailto:${cvDetail.privateInformation.email}`}
                            className="inline-block text-sm font-normal"
                          >
                            {cvDetail.privateInformation.email}
                          </a>
                        </div>
                      )}
                    </>
                  )}

                  {!isAllowedShortData && !cvDetail?.isOwnCV && (cvDetail?.privateInformation?.phone || cvDetail?.privateInformation?.email) && (
                    <div
                      role="presentation"
                      onClick={() => sendContactedData()}
                      className="flex flex-col items-center gap-y-1 text-sm font-normal text-ink-medium lg:items-start"
                    >
                      {cvDetail?.privateInformation?.phone && (
                        <div className="flex items-center gap-1">
                          <PhoneIcon className="size-4 shrink-0 text-ink-medium" />
                          <span>
                            {`${cvDetail?.privateInformation?.phone.slice(0, 5)} ... ${cvDetail?.privateInformation?.phone.slice(-3)}`}
                          </span>
                        </div>
                      )}

                      {cvDetail?.privateInformation?.email && (
                        <div className="flex items-center gap-1">
                          <EnvelopeIcon className="size-4 shrink-0 text-ink-medium" />
                          <span>
                            {`${cvDetail?.privateInformation?.email.replace(/^[^@]*/, '...')}`}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-2 flex flex-wrap items-center justify-start">
                {
                cvDetail?.privateInformation?.personalLinks
                && cvDetail.privateInformation.personalLinks.map((social) => {
                  if (!social.url?.length) {
                    return null;
                  }

                  return (
                    <Anchor key={social.id} href={social.url} styling="text" color="primary" target="_blank">
                      <div className="mb-2 mr-4 flex items-center text-sm text-ink">
                        {ResumeSocialIconById(social.id, 'w-4 h-4')}
                        <span className="ml-1 inline-block">{social.name}</span>
                      </div>
                    </Anchor>
                  );
                })
                }
              </div>

              {onDownloadCv && (
                <div className="mt-3">
                  <Button
                    styling="text"
                    color="ink"
                    onClick={() => onDownloadCv()}
                  >
                    <ArrowDownTrayIcon className="mr-2 size-5 text-ink" />
                    {t('global.action.download.label')}
                  </Button>
                </div>
              )}

              {cvDetail?.hiredConfirm && !hasBeenConfirmedFromHiredModal
              && (
                <>
                  <div
                    role="presentation"
                    className="my-2 mr-3 flex w-fit cursor-pointer items-center
                    rounded bg-secondary px-4 py-1 text-sm text-surface shadow-xl"
                    onClick={() => setSalaryModal(true)}
                  >
                    <CheckIcon width={15} className="mr-2 text-surface" />
                    {t('hired-applicant.confirm.btn')}
                  </div>

                  <Modal
                    size="sm"
                    isOpen={salaryModal}
                    header={t('confirm_hired.modal.title')}
                    body={(
                      <HireSalaryModal
                        applicantId={applicantID}
                        endpoint=""
                        onSendSuccess={succesfullHiredModal}
                        comingFrom="confirm_salary_id"
                        locale={locale}
                      />
                    )}
                    onClose={() => {
                      setSalaryModal(false);
                    }}
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* If the user has unpaid invoices let him know */}
      {unpaidInvoice !== undefined && unpaidInvoice?.orderInfo?.type === 'expired' && unpaidInvoice?.orderInfo?.orders?.length > 0 && unpaidInvoice?.bannedFromUsingServices
        && (
          <div className="flex items-center justify-start border-b p-4">
            <div className="w-10 min-w-10">
              <IconUnpaidOrExpired />
            </div>
            <div className="ml-2">
              <span className="block text-sm font-bold">{t('cv.detail.unpaid_or_expired_invoice')}</span>
              <span className="block text-tiny text-ink-medium">
                {`${t('cv.detail.unpaid.attachment')} `}
                {
                  unpaidInvoice?.orderInfo?.orders?.map((item) => (
                    <Anchor
                      key={item}
                      styling="text"
                      href={route('app_order_list', true)}
                      target={openLinksInNewTab ? '_blank' : undefined}
                    >
                      <span className="text-tiny text-primary">
                        {` ${item as string}, `}
                      </span>
                    </Anchor>
                  ))
                }
              </span>
            </div>
          </div>
        )}
    </>
  );
};
